import { useEffect, useRef, useState } from "react";
import { CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, TextField } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { SoftButton, cls, useDialogPaperSx } from "../hatim/soft/SoftKit";
import { useSearchQuestionsMutation } from "../../api/libraryApi";
import { TYPE_LABEL } from "../muzakere/questionRules";

const keyOf = (item) => `${item.packageId}:${item.question?.qid}`;

/**
 * Diğer paketlerin sorularında arama (mobil QuestionSearchModal): en az 2
 * harf, yazma durunca aranır; basılan soru listeye eklenir (onAdd false
 * dönerse eklenmedi: aynısı var ya da liste dolu). "Daha fazla" ile sayfa.
 */
export default function QuestionSearchDialog({ open, onClose, onAdd }) {
  const { t } = useScriptMode();
  const paperSx = useDialogPaperSx();
  const [search] = useSearchQuestionsMutation();
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [added, setAdded] = useState([]);
  const [skipped, setSkipped] = useState([]);
  // Geç gelen eski cevap yenisini ezmesin.
  const seq = useRef(0);

  useEffect(() => {
    if (open) {
      setAdded([]);
      setSkipped([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    const mine = ++seq.current;
    setLoadingMore(false);
    if (q.length < 2) {
      setItems([]);
      setHasMore(false);
      setLoading(false);
      setError(null);
      return undefined;
    }
    const handle = setTimeout(async () => {
      setLoading(true);
      setError(null);
      const result = await search({ q, page: 1 });
      if (mine !== seq.current) return;
      setLoading(false);
      if (result.error) {
        setError(result.error.message || t("lib_action_failed"));
        return;
      }
      setItems(result.data?.data?.items || []);
      setHasMore(Boolean(result.data?.data?.hasMore));
      setPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [query, open, search, t]);

  const loadMore = async () => {
    if (!hasMore || loading || loadingMore) return;
    const mine = seq.current;
    setLoadingMore(true);
    const result = await search({ q: query.trim(), page: page + 1 });
    if (mine !== seq.current) return;
    setLoadingMore(false);
    if (result.error) {
      setError(result.error.message || t("lib_action_failed"));
      return;
    }
    const more = result.data?.data?.items || [];
    setItems((prev) => {
      const seen = new Set(prev.map(keyOf));
      return [...prev, ...more.filter((i) => !seen.has(keyOf(i)))];
    });
    setPage(page + 1);
    setHasMore(Boolean(result.data?.data?.hasMore));
  };

  const add = (item) => {
    const key = keyOf(item);
    if (onAdd(item.question)) setAdded((list) => [...list, key]);
    else setSkipped((list) => [...list, key]);
  };

  const short = query.trim().length < 2;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 1500 }}
      PaperProps={{ sx: { borderRadius: 3, height: "min(640px, 90vh)", ...paperSx } }}
    >
      <DialogTitle sx={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 1 }}>
        <span className="flex-1">{t("lib_search_questions")}</span>
        <IconButton size="small" onClick={onClose} aria-label={t("lib_close")}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <TextField
          size="small"
          value={query}
          autoFocus
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("lib_search_question_placeholder")}
          inputProps={{ "aria-label": t("lib_search_question_placeholder"), maxLength: 80 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" className={cls.muted} />
              </InputAdornment>
            ),
          }}
        />
        {loading ? (
          <div className="py-6 flex justify-center">
            <CircularProgress size={22} />
          </div>
        ) : error && items.length === 0 ? (
          <p className={`text-sm ${cls.danger}`}>{error}</p>
        ) : short ? (
          <p className={`text-sm ${cls.muted}`}>{t("lib_search_min")}</p>
        ) : items.length === 0 ? (
          <p className={`text-sm ${cls.muted}`}>{t("lib_search_empty")}</p>
        ) : (
          <ul className="flex-1 min-h-0">
            {items.map((item) => {
              const key = keyOf(item);
              const done = added.includes(key);
              const skip = skipped.includes(key);
              return (
                <li key={key}>
                  <button
                    type="button"
                    disabled={done}
                    onClick={() => add(item)}
                    className={`w-full flex items-center gap-3 py-2.5 text-left ${cls.row} ${cls.rowHover}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm break-words ${cls.title}`}>{item.question?.text}</span>
                      <span className={`block text-xs truncate ${cls.muted}`}>
                        {`${item.packageTitle} · ${t(TYPE_LABEL[item.question?.type] || "")}`}
                      </span>
                      {skip && !done ? <span className={`block text-xs ${cls.muted}`}>{t("lib_search_skipped")}</span> : null}
                    </span>
                    {done ? (
                      <span className="text-xs text-[#2E9E6A] dark:text-[#7BE3A8]">{t("lib_search_added")}</span>
                    ) : (
                      <AddCircleOutlineIcon fontSize="small" className={cls.accent} />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {hasMore && !loading ? (
          <div className="flex justify-center">
            <SoftButton tone="soft" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? <CircularProgress size={18} color="inherit" /> : t("lib_more")}
            </SoftButton>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
